import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Download, Trash2, FileText, Shield, AlertTriangle } from 'lucide-react'
import { api } from '../../services/api'
import { supabase } from '../../services/supabase'
import { useAuthStore } from '../../store/authStore'
import { disconnectSocket } from '../../services/socket'

interface SettingsPanelProps {
  onClose: () => void
}

export default function SettingsPanel({ onClose }: SettingsPanelProps) {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const [exporting, setExporting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [error, setError] = useState('')

  const handleExportData = async () => {
    setExporting(true)
    setError('')
    try {
      const res = await api.get('/auth/export')
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `territory-nexus-data-${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setError('Failed to export data')
    } finally {
      setExporting(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteInput !== 'DELETE') return
    setDeleting(true)
    setError('')
    try {
      await api.delete('/auth/account')
      disconnectSocket()
      logout()
      await supabase.auth.signOut()
      navigate('/login')
    } catch {
      setError('Failed to delete account. Please try again.')
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[102] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-h-[85dvh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-white/10 bg-slate-900">
          <h2 className="text-lg font-bold text-white">Settings</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {error && (
            <div className="px-3 py-2 rounded-lg border border-red-500/50 bg-red-500/10 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Legal */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Legal</h3>
            <button
              onClick={() => { onClose(); navigate('/privacy') }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
            >
              <Shield className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-300">Privacy Policy</span>
            </button>
            <button
              onClick={() => { onClose(); navigate('/terms') }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
            >
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-300">Terms of Service</span>
            </button>
          </section>

          {/* Data */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Your Data</h3>
            <button
              onClick={handleExportData}
              disabled={exporting}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-slate-300">
                {exporting ? 'Exporting...' : 'Export My Data'}
              </span>
            </button>
          </section>

          {/* Danger Zone */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-red-400/70 uppercase tracking-wider">Danger Zone</h3>

            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-red-500/20 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400">Delete My Account</span>
              </button>
            ) : (
              <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/5 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-300">
                    This will permanently delete your account, all units, zones, structures, and game progress. This cannot be undone.
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-slate-400">Type DELETE to confirm:</label>
                  <input
                    type="text"
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                    placeholder="DELETE"
                    className="w-full px-3 py-2 bg-slate-800 border border-red-500/30 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-red-500/60"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setDeleteInput('') }}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-white/10 hover:bg-white/5 text-slate-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteInput !== 'DELETE' || deleting}
                    className="flex-1 px-3 py-2 text-sm rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {deleting ? 'Deleting...' : 'Delete Forever'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
